import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';

import { getDocumentName, getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType, GenericStatusId } from '@e-socna/data-layer';

import { SpeedDial } from '../../components/SpeedDial';
import { RightDrawer } from '../../components/RightDrawer';
import { Admin as AdminCmp } from '../../components/Admin';
import {
  PATH_RESOLVABLE_SERVICE_ADMIN,
  PATH_RESOLVABLE_SERVICE_ADMIN_EDIT,
} from '../../app/constants/paths.constant';
import { GenericAdminForm } from '../../forms/GenericAdmin';
import { useMemos } from '../../forms/MemoBeforeSubmit/hooks/useMemos.hook';
import { memoForm2ApiMapper } from '../../forms/MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { useGraphQLMutation } from '../../hooks/useGraphQLMutation.hook';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import { ResolvableServiceListCmp_AllResolvableServices_QueryDocument } from '../../components/ResolvableServiceList/hooks/useFindAllResolvableServicesQuery.hook';
import type { GenericAdminFormValues } from '../../forms/GenericAdmin';

import { ResolvableServicePage_OneResolvableService_QueryDocument } from './hooks/useFindOneResolvableServiceQuery.hook';

export const ResolvableServicePage_Admin_ResolvableServiceBaseInterface_Fragment =
  graphql(`
    fragment ResolvableServicePage_Admin_ResolvableServiceBaseInterface_Fragment on ResolvableServiceBaseInterface {
      _id
      statusId
      createdAt
      creatorId
      updatedAt
      updaterId
    }
  `);

export const ResolvableServicePage_Admin_ResolvableService_Foapu_Mutation =
  graphql(`
    mutation ResolvableServicePage_Admin_ResolvableService_Foapu(
      $filter: IdFilterInput!
      $update: ResolvableServicePartialInput!
      $versionData: VersionDataInput
    ) {
      findOneAndPartialUpdateResolvableService(
        filter: $filter
        update: $update
        versionData: $versionData
      ) {
        _id
      }
    }
  `);

interface Props {
  readonly resolvableService: FragmentType<
    typeof ResolvableServicePage_Admin_ResolvableServiceBaseInterface_Fragment
  >;
}

export const Admin = ({
  resolvableService: pResolvableService,
}: Readonly<Props>) => {
  const resolvableService = getFragmentData(
    ResolvableServicePage_Admin_ResolvableServiceBaseInterface_Fragment,
    pResolvableService,
  );

  const navigate = useNavigate();

  const { paths, currentPathKey } = useBuildPaths(
    {
      consult: PATH_RESOLVABLE_SERVICE_ADMIN,
      edit: PATH_RESOLVABLE_SERVICE_ADMIN_EDIT,
    },
    { id: resolvableService._id },
  );

  const isInEditionMode = currentPathKey === 'edit';
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  const { enqueueSnackbar } = useSnackbar();

  const { clearMemos, memos, setMemos } = useMemos();

  // mutations
  const { mutate: foapuResolvableServiceMutation } = useGraphQLMutation(
    ResolvableServicePage_Admin_ResolvableService_Foapu_Mutation,
  );

  // on success
  const queryClient = useQueryClient();

  const mutate = useCallback(
    (
      values: GenericAdminFormValues,
      handleError: (error: unknown) => unknown,
      handleSuccess: () => unknown,
    ) => {
      const onSuccess = () => {
        leaveEditionMode();
        clearMemos();
        enqueueSnackbar('Modification enregistrée.');
        handleSuccess();
        void queryClient.invalidateQueries({
          queryKey: [
            getDocumentName(
              ResolvableServicePage_OneResolvableService_QueryDocument,
            ),
            { id: resolvableService._id },
          ],
        });
        void queryClient.invalidateQueries({
          queryKey: [
            getDocumentName(
              ResolvableServiceListCmp_AllResolvableServices_QueryDocument,
            ),
          ],
        });
      };

      foapuResolvableServiceMutation(
        {
          filter: {
            _id: resolvableService._id,
          },
          update: values,
          versionData: memoForm2ApiMapper(memos),
        },
        {
          onError: handleError,
          onSuccess,
        },
      );
    },
    [
      clearMemos,
      enqueueSnackbar,
      foapuResolvableServiceMutation,
      leaveEditionMode,
      memos,
      queryClient,
      resolvableService._id,
    ],
  );

  return (
    <>
      <AdminCmp
        createdAt={resolvableService.createdAt}
        creatorId={resolvableService.creatorId}
        id={resolvableService._id}
        statusId={resolvableService.statusId}
        updatedAt={resolvableService.updatedAt}
        updaterId={resolvableService.updaterId}
      />
      <SpeedDial
        actions={[
          {
            id: 'resolvableServiceAdmin',
            icon: <DisplaySettingsIcon />,
            name: 'Administrer',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur l'Administration du Service"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <GenericAdminForm
          clearMemos={clearMemos}
          memos={memos}
          onCancel={leaveEditionMode}
          onSubmit={mutate}
          setMemos={setMemos}
          statusId={resolvableService.statusId as GenericStatusId}
        />
      </RightDrawer>
    </>
  );
};
